import {isAllowedProvider, Provider} from "../embed.provider";
import {EmbedStorage} from "./storage";

export class EmbedLocalStorage implements  EmbedStorage {
    readonly #ttlInMs: number = 180 * 24 * 60 * 60 * 1000;
    readonly #storageKey: string = 'sso_user_allowed_embeds';
    #consentedProviders: Set<Provider>;

    constructor() {
        this.#consentedProviders = this.readState() ?? new Set();
    }

    providerHasConsent(provider: Provider): boolean {
        return this.#consentedProviders.has(provider);
    }

    setProviderConsent(provider: Provider, allowed: boolean): void {
        if (!isAllowedProvider(provider)) return;
        if (allowed) {
            this.#consentedProviders.add(provider);
        } else {
            this.#consentedProviders.delete(provider);
        }
        this.writeState();
    }

    resetConsent(): void {
        this.#consentedProviders.clear();
        this.writeState();
    }

    /**
     * Retrieve list of consented embed providers from storage.
     * It will return nothing if key has no value in storage, data is invalid or is expired.
     * @private
     */
    private readState(): Set<Provider> | void {
        try {
            const data = localStorage.getItem(this.#storageKey);
            if (data) {
                const {expiry, consented} = JSON.parse(data);
                if (typeof consented !== 'string') return;
                if (Date.now() > expiry) {
                    this.resetConsent();
                    return;
                }
                return new Set<Provider>(consented
                    .split(',')
                    .filter((provider) => isAllowedProvider(provider))
                );
            }
        } catch {
            // Intentional — void is a proper response
        }
    }

    /**
     * Write comma separated providers with expiration to storage.
     * If none are consented we opt for removal of the entry.
     * @private
     */
    private writeState(): void {
        if (this.#consentedProviders.size === 0) {
            localStorage.removeItem(this.#storageKey);
        } else {
            localStorage.setItem(this.#storageKey, JSON.stringify({
                expiry: Date.now() + this.#ttlInMs,
                consented: Array.from(this.#consentedProviders).sort().join(','),
            }));
        }
    }
}
